// src/api/services/index.ts
// All typed API service calls — one place for all contracts

import { apiGet, apiPost, apiPut, apiDelete } from '../client';
import {
  AppointmentDetailDto,
  AppointmentDto,
  AppSettingDto,
  ApproveRejectRequest,
  AuthResponse,
  AvailableSlotDto,
  BulkUpdateSettingsRequest,
  CreateAppointmentRequest,
  CreateDoctorRequest,
  CreateStaffRequest,
  CreateLeaveRequest,
  CreateScheduleRequest,
  DashboardStatsDto,
  DepartmentDto,
  DoctorAdminDto,
  DoctorDetailDto,
  DoctorLeaveDto,
  DoctorListDto,
  DoctorMyPatientsData,
  FlexibleSlotDto,
  PatientAuthResponse,
  PatientLookupDto,
  PatientSendOtpRequest,
  PatientVerifyOtpRequest,
  PublicSettingsDto,
  QueueItemDto,
  ReceptionMyAppointment,
  ScheduleAdminDto,
  StaffDto,
  StaffLoginRequest,
  UpdateDoctorRequest,
  UpdateScheduleRequest,
  UpdateStaffRequest,
  AdminResetPasswordRequest,
  AllAppointmentDto,
  AppointmentActionLogDto,
  UpdateTokenResultDto,
  WalkInRequest,
} from '../types';

// ── Auth ─────────────────────────────────────────────────────────────────────
export const authService = {
  staffLogin: (req: StaffLoginRequest) =>
    apiPost<AuthResponse>('/api/auth/staff/login', req),

  sendOtp: (req: PatientSendOtpRequest) =>
    apiPost<null>('/api/auth/patient/send-otp', req),

  verifyOtp: (req: PatientVerifyOtpRequest) =>
    apiPost<PatientAuthResponse>('/api/auth/patient/verify-otp', req),
};

// ── Departments ───────────────────────────────────────────────────────────────
export const departmentService = {
  getAll: () =>
    apiGet<DepartmentDto[]>('/api/departments'),

  getBySlug: (slug: string) =>
    apiGet<DepartmentDto>(`/api/departments/${slug}`),
};

// ── Doctors ───────────────────────────────────────────────────────────────────
export const doctorService = {
  getAll: (params?: {
    departmentId?: number;
    category?: string;
    acceptingBookings?: boolean;
  }) => apiGet<DoctorListDto[]>('/api/doctors', params as Record<string, unknown>),

  getBySlug: (slug: string) =>
    apiGet<DoctorDetailDto>(`/api/doctors/${slug}`),

  getAvailableSlots: (id: number, from?: string, to?: string) =>
    apiGet<AvailableSlotDto[]>(`/api/doctors/${id}/slots`, {
      ...(from && { from }),
      ...(to && { to }),
    }),
};

// ── Flexible Slots ───────────────────────────────────────────────────────────
export const slotService = {
  getByDepartment: (departmentId: number, daysAhead = 7) =>
    apiGet<FlexibleSlotDto[]>('/api/slots/by-department', { departmentId, daysAhead }),

  getBySession: (session: string, departmentId?: number, daysAhead = 7) =>
    apiGet<FlexibleSlotDto[]>('/api/slots/by-session', {
      session,
      daysAhead,
      ...(departmentId && { departmentId }),
    }),
};

// ── Appointments ──────────────────────────────────────────────────────────────
export const appointmentService = {
  create: (req: CreateAppointmentRequest) =>
    apiPost<AppointmentDetailDto>('/api/appointments', req),

  getByNumber: (number: string) =>
    apiGet<AppointmentDetailDto>(`/api/appointments/${number}`),

  getMyAppointments: () =>
    apiGet<AppointmentDto[]>('/api/appointments/my'),

  cancel: (id: number, req?: ApproveRejectRequest) =>
    apiPut<AppointmentDetailDto>(`/api/appointments/${id}/cancel`, req),

  getActionLogs: (id: number) =>
    apiGet<AppointmentActionLogDto[]>(`/api/appointments/${id}/logs`),
};

// ── Reception ─────────────────────────────────────────────────────────────────
export const receptionService = {
  getDashboardStats: (date?: string) =>
    apiGet<DashboardStatsDto>('/api/reception/dashboard', date ? { date } : {}),

  patientLookup: (phone: string) =>
    apiGet<PatientLookupDto>('/api/reception/patient-lookup', { phone }),

  getPending: () =>
    apiGet<AppointmentDto[]>('/api/reception/pending'),

  getUpcoming: () =>
    apiGet<AppointmentDto[]>('/api/reception/upcoming'),

  bookForPatient: (req: CreateAppointmentRequest) =>
    apiPost<AppointmentDetailDto>('/api/reception/book', req),

  approve: (id: number, req?: ApproveRejectRequest) =>
    apiPut<AppointmentDetailDto>(`/api/reception/appointments/${id}/approve`, req),

  reject: (id: number, req?: ApproveRejectRequest) =>
    apiPut<AppointmentDetailDto>(`/api/reception/appointments/${id}/reject`, req),

  checkIn: (id: number) =>
    apiPut<AppointmentDetailDto>(`/api/reception/appointments/${id}/checkin`),

  walkIn: (req: WalkInRequest) =>
    apiPost<AppointmentDetailDto>('/api/reception/walkin', req),

  cancel: (id: number, req?: ApproveRejectRequest) =>
    apiPut<AppointmentDetailDto>(`/api/reception/appointments/${id}/cancel`, req),

  startConsultation: (id: number) =>
    apiPut<AppointmentDetailDto>(`/api/reception/appointments/${id}/start`),

  completeConsultation: (id: number) =>
    apiPut<AppointmentDetailDto>(`/api/reception/appointments/${id}/complete`),

  getMyAppointments: () =>
    apiGet<ReceptionMyAppointment[]>('/api/reception/my-appointments'),

  updateToken: (id: number, tokenNumber: number) =>
    apiPut<UpdateTokenResultDto>(`/api/reception/appointments/${id}/token`, { tokenNumber }),

  getAllAppointments: (params?: {
    from?: string; to?: string; status?: string;
    doctorId?: number; departmentId?: number; search?: string;
  }) =>
    apiGet<AllAppointmentDto[]>('/api/reception/all', params),
};

// ── Queue ─────────────────────────────────────────────────────────────────────
export const queueService = {
  getToday: () =>
    apiGet<QueueItemDto[]>('/api/queue/today'),

  getDoctorQueue: (doctorId: number) =>
    apiGet<QueueItemDto[]>(`/api/queue/doctor/${doctorId}`),
};

// ── Doctor Admin ─────────────────────────────────────────────────────────────
export const doctorAdminService = {
  getAll: () => apiGet<DoctorAdminDto[]>('/api/admin/doctors'),
  getById: (id: number) => apiGet<DoctorAdminDto>(`/api/admin/doctors/${id}`),
  create: (req: CreateDoctorRequest) => apiPost<DoctorAdminDto>('/api/admin/doctors', req),
  update: (id: number, req: UpdateDoctorRequest) => apiPut<DoctorAdminDto>(`/api/admin/doctors/${id}`, req),
  toggleActive: (id: number) => apiPut<DoctorAdminDto>(`/api/admin/doctors/${id}/toggle-active`),
  getSchedules: (id: number) => apiGet<ScheduleAdminDto[]>(`/api/admin/doctors/${id}/schedules`),
  createSchedule: (doctorId: number, req: CreateScheduleRequest) => apiPost<ScheduleAdminDto>(`/api/admin/doctors/${doctorId}/schedules`, req),
  updateSchedule: (scheduleId: number, req: UpdateScheduleRequest) => apiPut<ScheduleAdminDto>(`/api/admin/doctors/schedules/${scheduleId}`, req),
  deleteSchedule: (scheduleId: number) => apiDelete<void>(`/api/admin/doctors/schedules/${scheduleId}`),
  getLeaves: (id: number) => apiGet<DoctorLeaveDto[]>(`/api/admin/doctors/${id}/leaves`),
  createLeave: (doctorId: number, req: CreateLeaveRequest) => apiPost<DoctorLeaveDto>(`/api/admin/doctors/${doctorId}/leaves`, req),
  deleteLeave: (leaveId: number) => apiDelete<void>(`/api/admin/doctors/leaves/${leaveId}`),
};

// ── Staff Admin ──────────────────────────────────────────────────────────────
export const staffAdminService = {
  getAll: () => apiGet<StaffDto[]>('/api/admin/staff'),
  getById: (id: number) => apiGet<StaffDto>(`/api/admin/staff/${id}`),
  getByDoctorId: (doctorId: number) => apiGet<StaffDto>(`/api/admin/staff/by-doctor/${doctorId}`),
  create: (req: CreateStaffRequest) => apiPost<StaffDto>('/api/admin/staff', req),
  update: (id: number, req: UpdateStaffRequest) => apiPut<StaffDto>(`/api/admin/staff/${id}`, req),
  toggleActive: (id: number) => apiPut<StaffDto>(`/api/admin/staff/${id}/toggle-active`),
  resetPassword: (id: number, req: AdminResetPasswordRequest) => apiPut<string>(`/api/admin/staff/${id}/reset-password`, req),
};

// ── Settings ─────────────────────────────────────────────────────────────────
export const settingsService = {
  getPublic: () =>
    apiGet<PublicSettingsDto>('/api/settings/public'),

  getAll: () =>
    apiGet<AppSettingDto[]>('/api/settings'),

  getByCategory: (category: string) =>
    apiGet<AppSettingDto[]>(`/api/settings/${category}`),

  bulkUpdate: (req: BulkUpdateSettingsRequest) =>
    apiPut<AppSettingDto[]>('/api/settings', req),
};

// ── Doctor Dashboard ──────────────────────────────────────────────────────────
export const doctorDashboardService = {
  getToday: () =>
    apiGet<QueueItemDto[]>('/api/doctor/today'),

  getMyPatients: () =>
    apiGet<DoctorMyPatientsData>('/api/doctor/my-patients'),

  startConsultation: (id: number) =>
    apiPut<AppointmentDetailDto>(`/api/doctor/appointments/${id}/start`),

  completeConsultation: (id: number) =>
    apiPut<AppointmentDetailDto>(`/api/doctor/appointments/${id}/complete`),
};
